import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { EmptyStateRadar } from '@rossum/ui';
import { IconColumns } from '@rossum/ui/icons/tabler';
import { Button, Stack, Typography } from '@rossum/ui/material';
import { linebreak } from '../../../lib/formaterValues';
import { ColumnTile } from '../columns/ColumnTile';
import { CreateForm } from './createFormSchema';
import { generateDatasetName } from './generateDatasetName';

type ColumnsProps = {
  control: Control<CreateForm>;
  setValue: UseFormSetValue<CreateForm>;
};

// Declared columns make the dataset check uploaded data against them; without
// any, the columns are taken from the uploaded file.
export const Columns = ({ control, setValue }: ColumnsProps) => {
  const intl = useIntl();
  const { fields, prepend, remove, move } = useFieldArray({
    control,
    name: 'columns',
  });
  // Live values for the column headers; `fields` only holds the initial ones.
  const columns = useWatch({ control, name: 'columns' });

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = fields.findIndex(field => field.id === active.id);
    const to = fields.findIndex(field => field.id === over.id);
    if (from !== -1 && to !== -1) move(from, to);
  };

  return (
    <ContentGroup
      isFullWidthLayout
      title={intl.formatMessage({
        id: 'features.datasets.create.columns.title',
      })}
      description={
        <Typography variant="inherit">
          {intl.formatMessage({
            id: 'features.datasets.create.columns.description',
          })}
        </Typography>
      }
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={() =>
              prepend({ label: '', attributeName: '', type: 'string' })
            }
            data-cy="dataset-create-column-add"
          >
            {intl.formatMessage({ id: 'features.datasets.create.columns.add' })}
          </Button>
        </Stack>
        {fields.length === 0 ? (
          <Stack sx={{ height: 400 }}>
            <EmptyStateRadar
              title={intl.formatMessage({
                id: 'features.datasets.create.columns.empty.title',
              })}
              description={intl.formatMessage(
                { id: 'features.datasets.create.columns.empty.description' },
                { linebreak }
              )}
              iconProps={{ Icon: IconColumns, iconSize: 24 }}
            />
          </Stack>
        ) : (
          <DndContext
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={fields.map(field => field.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1}>
                {fields.map((field, index) => (
                  <ColumnTile
                    key={field.id}
                    control={control}
                    labelField={`columns.${index}.label`}
                    attributeName={columns[index]?.attributeName ?? ''}
                    type={columns[index]?.type ?? 'string'}
                    declared={{
                      attributeNameField: `columns.${index}.attributeName`,
                      typeField: `columns.${index}.type`,
                      onLabelBlur: () => {
                        // Never overwrite an attribute the user has set.
                        const generated = generateDatasetName(
                          columns[index]?.label ?? ''
                        );
                        if (generated && !columns[index]?.attributeName) {
                          setValue(
                            `columns.${index}.attributeName`,
                            generated,
                            { shouldValidate: true }
                          );
                        }
                      },
                      onDelete: () => remove(index),
                      dragId: field.id,
                    }}
                    data-cy="dataset-create-column"
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
      </Stack>
    </ContentGroup>
  );
};
